import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, filter, map, Observable, take } from 'rxjs';
import { TenantConfigStore } from '../../shared/stores/tenant-config.store';
import { EnvironmentService } from '../services/environment.service';
import { AuthService } from '../services/auth.service';
import { PreviewModeService } from '../services/preview-mode.service';

/**
 * Ensures the tenant portal configuration is loaded before any auth-protected
 * route activates. Returns immediately if already loaded (cached in store).
 * The home page triggers a background refresh that updates the service cache;
 * after logout + re-login the guard fetches fresh from the API.
 *
 * - No session yet        → returns true, leaving the redirect to `authGuard` (see below).
 * - Already loaded        → returns true synchronously (no flicker).
 * - Successful load       → returns true (navigation proceeds).
 * - Load error            → redirects to /error?code=api-unavailable.
 *
 * ## Why the no-session check matters
 *
 * This guard shares a `canActivate` array with `authGuard`, and Angular runs the guards in an
 * array **concurrently** — so without the check below, an unauthenticated visitor following a
 * login-required form link fires `GET /api/v1/portal-config` (an `[Authorize]` endpoint) before
 * they have a token. That 401 lands in {@link TenantConfigStore}'s `catchError`, which sets
 * `error` and leaves it set until logout calls `reset()`. Two visible consequences followed:
 *
 * 1. The login page rendered **without tenant branding**, because the store held an error and no
 *    `logoUrl`.
 * 2. After a *successful* login the next navigation hit this guard again, and
 *    `toObservable(store.error)` replayed that stale error immediately — so the guard resolved to
 *    `/error?code=api-unavailable` before the fresh, authenticated request could finish.
 *
 * Returning `true` here is safe precisely because `authGuard` sits earlier in the same array:
 * Angular's `prioritizedGuardValue` prefers the first guard that resolves to a non-`true` value,
 * so `authGuard`'s login redirect still wins. Preview mode is excluded from the check because
 * `PortalConfigService.load()` serves it from the admin's preview payload rather than the API,
 * and a preview tab legitimately has no token.
 */
export const portalConfigGuard: CanActivateFn = (): boolean | Observable<boolean | UrlTree> => {
  const store = inject(TenantConfigStore);
  const router = inject(Router);
  const env = inject(EnvironmentService);
  const authService = inject(AuthService);
  const previewMode = inject(PreviewModeService);

  // DEV-ONLY: offline bypass — tenant portal-config is served by the VPN-bound EP
  // API and 401s with the PM token, which would redirect to /error. Skip the
  // requirement; the shell falls back to default branding.
  if (env.devAuthBypass) {
    return true;
  }

  // No session and not a preview tab — requesting an authenticated resource now would only
  // produce a 401 that poisons the store for the rest of the session. Let authGuard redirect.
  if (!authService.isAuthenticated && !previewMode.isPreview()) {
    return true;
  }

  if (store.loaded()) {
    return true;
  }

  store.load();

  return combineLatest([
    toObservable(store.loaded),
    toObservable(store.error),
  ]).pipe(
    filter(([loaded, error]) => loaded || error !== null),
    take(1),
    map(([loaded]) =>
      loaded ? true : router.createUrlTree(['/error'], { queryParams: { code: 'api-unavailable' } })
    ),
  );
};
